import { useEffect, useRef, useState } from 'react'

/**
 * Pull down at the top of the page to reload — the web equivalent of the mini
 * program's `onPullDownRefresh` / `enablePullDownRefresh`.
 *
 * Why it is hand-rolled rather than left to the browser: Chrome's native
 * pull-to-refresh reloads the whole DOCUMENT. In a single-page app that throws
 * away the session state, the open tab, the scroll position and any
 * in-progress draft, then re-downloads the bundle — the opposite of what the
 * gesture means here. `overscroll-behavior-y: none` (already set on body for
 * the WeChat rubber-band) suppresses it, so this supplies the intended
 * behaviour: refetch the data, keep the app alive.
 *
 * Touch only. A desktop mouse has no pull gesture and a wheel-based imitation
 * fires constantly by accident.
 */

const TRIGGER_PX = 70      // far enough that a scroll-up cannot trip it
const MAX_PULL_PX = 110    // where the indicator stops following the finger

export function usePullToRefresh(onRefresh: () => Promise<unknown> | unknown) {
  const [pull, setPull] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const startY = useRef<number | null>(null)
  // Refs, not state: the touch handlers are bound once and would otherwise
  // close over the values from the render that attached them.
  const refreshingRef = useRef(false)
  const handler = useRef(onRefresh)
  handler.current = onRefresh
  // touchend must read the LATEST pull distance, not the one captured when the
  // listener was attached. Declared here, above the effect that closes over it.
  const pullRef = useRef(0)
  pullRef.current = pull

  useEffect(() => {
    const atTop = () => window.scrollY <= 0

    const onTouchStart = (event: TouchEvent) => {
      // Only arm the gesture when ALREADY at the top. Starting mid-page and
      // scrolling up into the top would otherwise trigger a refresh the
      // person never asked for.
      startY.current = atTop() && !refreshingRef.current ? event.touches[0].clientY : null
    }

    const onTouchMove = (event: TouchEvent) => {
      if (startY.current === null) return
      const distance = event.touches[0].clientY - startY.current
      if (distance <= 0) { setPull(0); return }
      if (!atTop()) { startY.current = null; setPull(0); return }
      // Resistance, so it feels like pulling against something rather than
      // dragging a free element.
      setPull(Math.min(MAX_PULL_PX, distance * 0.45))
    }

    const onTouchEnd = async () => {
      const shouldRefresh = pullRef.current >= TRIGGER_PX
      startY.current = null
      setPull(0)
      if (!shouldRefresh || refreshingRef.current) return
      refreshingRef.current = true
      setRefreshing(true)
      try {
        await handler.current()
      } finally {
        refreshingRef.current = false
        setRefreshing(false)
      }
    }

    // `passive: true` — this never calls preventDefault (overscroll-behavior
    // already stops the native pull), and a non-passive touchmove listener
    // makes scrolling janky on every frame.
    window.addEventListener('touchstart', onTouchStart, { passive: true })
    window.addEventListener('touchmove', onTouchMove, { passive: true })
    window.addEventListener('touchend', onTouchEnd)
    window.addEventListener('touchcancel', onTouchEnd)
    return () => {
      window.removeEventListener('touchstart', onTouchStart)
      window.removeEventListener('touchmove', onTouchMove)
      window.removeEventListener('touchend', onTouchEnd)
      window.removeEventListener('touchcancel', onTouchEnd)
    }
  }, [])

  return { pull, refreshing, active: pull >= TRIGGER_PX }
}
